/**
 * Ordinea, filtrarea si cautarea cardurilor de restaurant — logica PURA a
 * blocului „Restaurante", separata de randare ca sa poata fi testata.
 *
 * Ce decide ordinea „recomandata", in ordinea importantei:
 *  1. livreaza la adresa clientului (cand se stie) — un restaurant care nu
 *     ajunge la el nu are ce cauta in fata, oricat de bun ar fi;
 *  2. primeste comenzi acum (nu e in pauza);
 *  3. timpul estimat, cel mai scurt primul;
 *  4. numele, ca doua incarcari sa dea aceeasi ordine.
 *
 * Restaurantele care NU livreaza la adresa raman in lista, la coada, marcate:
 * cine a apasat pe un card gri stie de ce, in loc sa descopere in meniu. E ce
 * face si Wolt cu localurile inchise — le coboara, nu le ascunde.
 */

import type { StorefrontCoverageEntryDto, StorefrontSummaryDto } from "@deliveryapp/shared";

export type StorefrontSort = "recommended" | "eta" | "fee" | "name";

export const STOREFRONT_SORT_LABELS: Record<StorefrontSort, string> = {
  recommended: "Recomandate",
  eta: "Cele mai rapide",
  fee: "Livrare ieftină",
  name: "Alfabetic",
};

export function isStorefrontSort(value: string): value is StorefrontSort {
  return value === "recommended" || value === "eta" || value === "fee" || value === "name";
}

/** Fara diacritice, litere mici — aceeasi normalizare ca la cautarea din meniu. */
export function normalizeSearchText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

/**
 * Etichetele de bucatarie ale pietei, ca filtre, cel mult `max`.
 *
 * Un filtru e util cand IMPARTE lista. O eticheta prezenta pe toate
 * restaurantele („Bauturi" — toate au bauturi) nu imparte nimic si nu se
 * ofera; una prezenta pe aproape toate coboara la coada. Restul, cele mai
 * frecvente primele, cu ordine stabila la egalitate.
 */
export function collectTags(storefronts: readonly StorefrontSummaryDto[], max = 10): string[] {
  const counts = new Map<string, number>();
  let tagged = 0;
  for (const storefront of storefronts) {
    const tags = storefront.listing?.tags ?? [];
    if (tags.length > 0) tagged += 1;
    for (const tag of tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  const nearlyUniversal = Math.max(2, Math.ceil(tagged * 0.75));
  return [...counts.entries()]
    .filter(([, count]) => tagged < 2 || count < tagged)
    .sort(([a, countA], [b, countB]) => {
      const aCommon = countA >= nearlyUniversal ? 1 : 0;
      const bCommon = countB >= nearlyUniversal ? 1 : 0;
      return aCommon - bCommon || countB - countA || a.localeCompare(b, "ro");
    })
    .slice(0, max)
    .map(([tag]) => tag);
}

export interface StorefrontFilter {
  search: string;
  tag: string | null;
  /** Doar cele care livreaza la adresa; are efect numai cand acoperirea se stie. */
  deliverableOnly: boolean;
}

export const EMPTY_STOREFRONT_FILTER: StorefrontFilter = { search: "", tag: null, deliverableOnly: false };

export function matchesStorefrontFilter(
  storefront: StorefrontSummaryDto,
  filter: StorefrontFilter,
  coverage: StorefrontCoverageEntryDto | null,
): boolean {
  if (filter.tag !== null && !(storefront.listing?.tags ?? []).includes(filter.tag)) return false;
  if (filter.deliverableOnly && coverage !== null && coverage.status !== "deliverable") return false;
  const needle = normalizeSearchText(filter.search);
  if (needle.length === 0) return true;
  const haystack = normalizeSearchText(`${storefront.name} ${(storefront.listing?.tags ?? []).join(" ")}`);
  return haystack.includes(needle);
}

function etaOf(storefront: StorefrontSummaryDto, coverage: StorefrontCoverageEntryDto | null): number {
  if (coverage !== null && coverage.status === "deliverable" && coverage.etaMinutes !== null) return coverage.etaMinutes;
  const listing = storefront.listing;
  if (listing?.etaMinutesFrom === null || listing?.etaMinutesFrom === undefined) return Number.POSITIVE_INFINITY;
  return listing.etaMinutesFrom;
}

function feeOf(storefront: StorefrontSummaryDto, coverage: StorefrontCoverageEntryDto | null): number {
  if (coverage !== null && coverage.zone !== null) return coverage.zone.deliveryFee;
  const fee = storefront.listing?.deliveryFeeFrom;
  return fee === null || fee === undefined ? Number.POSITIVE_INFINITY : fee;
}

function deliverableRank(coverage: StorefrontCoverageEntryDto | null): number {
  if (coverage === null) return 1;
  if (coverage.status === "deliverable") return 0;
  if (coverage.status === "no_vendor") return 2;
  return 3;
}

export function sortStorefronts(
  storefronts: readonly StorefrontSummaryDto[],
  sort: StorefrontSort,
  coverageFor: (slug: string) => StorefrontCoverageEntryDto | null,
): StorefrontSummaryDto[] {
  const decorated = storefronts.map((storefront, index) => ({ storefront, index, coverage: coverageFor(storefront.slug) }));
  const byName = (a: StorefrontSummaryDto, b: StorefrontSummaryDto): number => a.name.localeCompare(b.name, "ro");
  decorated.sort((a, b) => {
    // Cele care nu livreaza la adresa coboara MEREU, oricare ar fi sortarea:
    // „cele mai rapide" nu are voie sa inceapa cu un restaurant care nu ajunge.
    const rank = deliverableRank(a.coverage) - deliverableRank(b.coverage);
    if (rank !== 0) return rank;
    const accepting = Number(b.storefront.listing?.acceptingOrders ?? true) - Number(a.storefront.listing?.acceptingOrders ?? true);
    if (accepting !== 0) return accepting;
    switch (sort) {
      case "eta": {
        const delta = etaOf(a.storefront, a.coverage) - etaOf(b.storefront, b.coverage);
        return delta !== 0 ? delta : byName(a.storefront, b.storefront);
      }
      case "fee": {
        const delta = feeOf(a.storefront, a.coverage) - feeOf(b.storefront, b.coverage);
        return delta !== 0 ? delta : byName(a.storefront, b.storefront);
      }
      case "name":
        return byName(a.storefront, b.storefront);
      default: {
        // Recomandate: cu adresa, dupa timp; fara adresa, ordinea publicata
        // (operatorul o alege din Hub), ca sa nu para ca platforma favorizeaza.
        if (a.coverage !== null && b.coverage !== null) {
          const delta = etaOf(a.storefront, a.coverage) - etaOf(b.storefront, b.coverage);
          if (delta !== 0) return delta;
        }
        return a.index - b.index;
      }
    }
  });
  return decorated.map((entry) => entry.storefront);
}

// ---------------------------------------------------------------------------
// Poarta de adresa a listei de restaurante
// ---------------------------------------------------------------------------

export interface StorefrontGateInput {
  /** Clientul si-a pus adresa in sesiune. */
  hasAddress: boolean;
  coverageState: "unknown" | "loading" | "ready";
  /** Blocul cere adresa inainte de a arata restaurantele (implicit da). */
  requireAddress: boolean;
  /** Blocul ascunde restaurantele care nu livreaza la adresa (implicit da). */
  hideUndeliverable: boolean;
  /** Clientul a cerut explicit „vezi toate restaurantele". */
  showAll: boolean;
}

export type StorefrontGate =
  | { kind: "ask_address" }
  | { kind: "checking" }
  | { kind: "list"; filter: "deliverable" | "all" };

/**
 * Ce arata blocul „Restaurante" in functie de adresa si de acoperire.
 *
 * Regula ceruta de produs: restaurantele se vad DUPA adresa, si doar cele care
 * livreaza la ea. Cele doua exceptii sunt deliberate: cand acoperirea nu se
 * poate afla (core mai vechi, cererea a picat) lista se arata intreaga — „nu
 * stiu" nu se afiseaza niciodata ca „nu livram"; si cand clientul cere singur
 * sa le vada pe toate, le vede, cu cele nelivrabile la coada si marcate.
 */
export function resolveStorefrontGate(input: StorefrontGateInput): StorefrontGate {
  if (input.requireAddress && !input.hasAddress) return { kind: "ask_address" };
  if (!input.hasAddress || !input.hideUndeliverable || input.showAll) return { kind: "list", filter: "all" };
  if (input.coverageState === "loading") return { kind: "checking" };
  if (input.coverageState === "unknown") return { kind: "list", filter: "all" };
  return { kind: "list", filter: "deliverable" };
}

/**
 * Doar restaurantele care livreaza la adresa. O vitrina FARA intrare in raspunsul
 * de acoperire (lista taiata de plafon) ramane: nu s-a verificat, nu s-a refuzat.
 */
export function onlyDeliverable(
  storefronts: readonly StorefrontSummaryDto[],
  coverageFor: (slug: string) => StorefrontCoverageEntryDto | null,
): StorefrontSummaryDto[] {
  return storefronts.filter((storefront) => {
    const entry = coverageFor(storefront.slug);
    return entry === null || entry.status === "deliverable";
  });
}

export interface StorefrontCardFacts {
  /** „25–35 min" sau „~28 min"; `null` cand nu se stie. */
  eta: string | null;
  /** `true` cand timpul e cel al adresei, nu unul orientativ. */
  etaIsForAddress: boolean;
  fee: { amount: number; from: boolean } | null;
  minOrder: number | null;
  freeDeliveryFrom: number | null;
  tags: string[];
  /** Ce se spune despre acoperire; `null` cand nu se stie. */
  coverage: "deliverable" | "out_of_coverage" | "no_vendor" | null;
  acceptingOrders: boolean;
  promoCount: number;
  offerCount: number;
}

/** Ce scrie pe card, combinand listarea (fara adresa) cu acoperirea (cu adresa). */
export function storefrontCardFacts(
  storefront: StorefrontSummaryDto,
  coverage: StorefrontCoverageEntryDto | null,
): StorefrontCardFacts {
  const listing = storefront.listing ?? null;
  const deliverable = coverage !== null && coverage.status === "deliverable";
  let eta: string | null = null;
  if (deliverable && coverage.etaMinutes !== null) {
    eta = `~${coverage.etaMinutes} min`;
  } else if (listing !== null && listing.etaMinutesFrom !== null && listing.etaMinutesTo !== null) {
    eta = `${listing.etaMinutesFrom}–${listing.etaMinutesTo} min`;
  }
  const fee =
    coverage !== null && coverage.zone !== null
      ? { amount: coverage.zone.deliveryFee, from: false }
      : listing !== null && listing.deliveryFeeFrom !== null
        ? { amount: listing.deliveryFeeFrom, from: true }
        : null;
  return {
    eta,
    etaIsForAddress: deliverable && coverage.etaMinutes !== null,
    fee,
    minOrder: coverage?.zone?.minOrderValue ?? listing?.minOrderFrom ?? null,
    freeDeliveryFrom: coverage?.zone?.freeDeliveryThreshold ?? listing?.freeDeliveryFrom ?? null,
    tags: listing?.tags ?? [],
    coverage: coverage === null ? null : coverage.status,
    acceptingOrders: listing?.acceptingOrders ?? true,
    promoCount: listing?.promoCount ?? 0,
    offerCount: listing?.offerCount ?? 0,
  };
}
